import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import productsByBoothId from 'network/productsByBoothId';
import { Product, LocalizedProduct } from 'models/types/products';
import getLocalizedProduct from 'models/helpers/getLocalizedProduct';
import { selectLocale } from './localization';

const boothProductsAdapter = createEntityAdapter<Product>({
  sortComparer: (a, b) => (a.sort || 0) - (b.sort || 0),
});
export const {
  selectAll: selectAllProducts,
} = boothProductsAdapter.getSelectors((state: any) => state.boothProducts);

export const selectBoothProductsByBoothId = (boothId: string) => (
  createSelector(
    [selectAllProducts, selectLocale],
    (products: Product[], locale: string) => products
      .filter((product) => product.boothId === boothId)
      .map((product) => getLocalizedProduct(product, locale) as LocalizedProduct),
  )
);

export const fetchBoothProducts = createAsyncThunk(
  'products/fetchBoothProducts',
  async (boothId: string) => {
    try {
      const result = await productsByBoothId(boothId);
      return result;
    } catch (error) {
      console.error(error);
    }
    return {};
  },
  {
    condition: (boothId, { getState }) => {
      const state: any = getState();
      const { fetching } = state.boothProducts;
      const boothProducts = selectBoothProductsByBoothId(boothId)(state);
      // If there are already booth products data, then no need to re-fetch
      return !fetching && (!boothProducts || !boothProducts.length);
    },
  },
);

export const selectBoothProductsByLocale = createSelector(
  [selectAllProducts, selectLocale],
  (products: Product[], locale: string) => products.map(
    (product) => getLocalizedProduct(product, locale) as LocalizedProduct,
  ),
);

export const boothProductSlice = createSlice({
  name: 'boothProducts',
  initialState: boothProductsAdapter.getInitialState({ fetching: false }),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchBoothProducts.pending, (state: any) => {
      state.fetching = true;
    });
    builder.addCase(fetchBoothProducts.rejected, (state: any) => {
      state.fetching = false;
    });
    builder.addCase(fetchBoothProducts.fulfilled, (state: any, action) => {
      state.fetching = false;
      boothProductsAdapter.upsertMany(state, action.payload);
    });
  },
});

export default boothProductSlice.reducer;
